import MenuItem from '@mui/material/MenuItem';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ConfirmationModal from '~/components/ConfirmationModal';
import ShiftPlanningIssuesDialog from '~/components/ShiftPlanningIssuesDialog';
import appMessages from '~/containers/App/messages';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import {
  cutoffDatesFinalizeUntil,
  cutoffDatesFinalizeUntilDryRun,
  ShiftVerificationResultResource,
} from '~/generated';
import messages from './messages';
import { extractData } from '~/utils/query-utils';

type FinalizeWeekItemProps = {
  endOfWeekDate: string;

  onCloseMenu: () => void;
};

export const FinalizeWeekMenuItem = (props: FinalizeWeekItemProps) => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const [
    shouldShowShiftPlanningIssuesDialog,
    setShouldShowShiftPlanningIssuesDialog,
  ] = useState(false);
  const [shouldShowConfirmationModal, setShouldShowConfirmationModal] =
    useState(false);
  const [planningIssues, setPlanningIssues] = useState<
    ShiftVerificationResultResource[] | undefined
  >();

  const showPlanningIssuesDialog = (
    issues: ShiftVerificationResultResource[]
  ) => {
    setShouldShowShiftPlanningIssuesDialog(true);
    setPlanningIssues(issues);
  };

  const hidePlanningIssuesDialog = () => {
    setShouldShowShiftPlanningIssuesDialog(false);
    setPlanningIssues(undefined);
  };

  const finalizationDryRun = useMutation({
    mutationKey: ['cutOff', 'finalization', 'dryRun'],
    mutationFn: () =>
      extractData(
        cutoffDatesFinalizeUntilDryRun({
          body: {
            until: props.endOfWeekDate,
          },
        })
      ),
    onSuccess(data) {
      const someShiftInRangeHasIssues = data.planningIssues?.some(
        (issuesForShift) =>
          (issuesForShift.shiftPlanningIssues?.length || 0) > 0
      );
      if (data.planningIssues && someShiftInRangeHasIssues) {
        showPlanningIssuesDialog(data.planningIssues);
      } else {
        setShouldShowConfirmationModal(true);
      }
      props.onCloseMenu();
    },
  });
  const finalization = useMutation({
    mutationKey: ['cutOff', 'finalization'],
    mutationFn: () =>
      extractData(
        cutoffDatesFinalizeUntil({
          body: {
            until: props.endOfWeekDate,
          },
        })
      ),
    onSuccess() {
      showSnackbar({
        message: intl.formatMessage(messages.finalizeWeekSuccess),
      });
      props.onCloseMenu();
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.finalizeWeekFailure),
      });
      props.onCloseMenu();
    },
  });

  return (
    <>
      <ShiftPlanningIssuesDialog
        planningIssues={planningIssues}
        onClose={hidePlanningIssuesDialog}
        isOpen={shouldShowShiftPlanningIssuesDialog}
      />
      <div>
        <MenuItem
          onClick={() => finalizationDryRun.mutate()}
          disabled={finalizationDryRun.isPending}
        >
          {finalizationDryRun.isPending ? (
            <FormattedMessage {...appMessages.loading} />
          ) : (
            <FormattedMessage {...messages.finalizeWeek} />
          )}
        </MenuItem>
        <ConfirmationModal
          open={shouldShowConfirmationModal}
          title={intl.formatMessage(messages.finalizeWeekConfirmationTitle)}
          text={<FormattedMessage {...messages.finalizeWeekConfirmationText} />}
          isConfirming={finalization.isPending}
          confirmLabel={
            finalization.isPending ? (
              <FormattedMessage {...appMessages.loading} />
            ) : (
              <FormattedMessage {...messages.finalizeWeekConfirmationButton} />
            )
          }
          cancelLabel={
            <FormattedMessage {...messages.finalizeWeekConfirmationCancel} />
          }
          onCancel={() => setShouldShowConfirmationModal(false)}
          onConfirm={() => finalization.mutate()}
        />
      </div>
    </>
  );
};

FinalizeWeekMenuItem.displayName = 'FinalizeWeekMenuItem';

export default FinalizeWeekMenuItem;
